import { useLocalization } from "@fluent/react";
import { Row, Chip } from "@narmi/design_system";
import { catcat as cc, ContentCard } from "cerulean";
import Filters from "byzantine/src/filters";
import utils from "byzantine/src/utils";
import { countryCodeToFlag } from "byzantine/src/dbbl/utils/internationalPhoneNumber";
import styles from "../../DualApproval.module.scss";
import parseTemplateName from "../../../PaymentActivity/common/templateNameUtils";
import ReversalDetails from "./ReversalDetails";
import PaymentScheduleDetails from "./PaymentScheduleDetails";
import type { TopCardProps } from "./types";

const TopCard = ({
  dualApproval,
  isPendingDrawer = true,
  originalPayment,
  handleClickOriginalPayment,
}: TopCardProps) => {
  const { l10n } = useLocalization();
  let typeCopy = l10n.getString("approval-drawer-standard-ach-label");

  if (dualApproval.wireId) {
    typeCopy = l10n.getString("fedwire-label");
  } else if (dualApproval.internationalWireId) {
    typeCopy = l10n.getString("international-wire-label");
  } else if (dualApproval.achReversalId) {
    typeCopy = l10n.getString("approval-drawer-ach-reversal-label");
  }

  const { fxQuote } = dualApproval;
  const hasFxQuote = !!(fxQuote && fxQuote.convertedAmount);
  const parsedTemplateName = dualApproval.payment?.templateName
    ? parseTemplateName(dualApproval.payment.templateName)
    : null;

  const getFxDisplayValues = () => {
    if (!fxQuote || !fxQuote.convertedAmount) return null;
    const isUsdSend = fxQuote.specifiedCurrency === "USD";

    const foreignCurrency = isUsdSend
      ? fxQuote.convertedCurrency
      : fxQuote.specifiedCurrency;
    const foreignAmount = isUsdSend
      ? fxQuote.convertedAmount
      : fxQuote.specifiedAmount;
    const usdAmount = isUsdSend
      ? fxQuote.specifiedAmount
      : fxQuote.convertedAmount;

    const foreignCurrencyCode = foreignCurrency.substring(0, 2);

    return {
      headlinePrefix: isUsdSend ? "~" : "",
      headlineAmount: utils.currencyFormattedValue(
        foreignAmount,
        foreignCurrency,
      ),
      headlineCurrency: foreignCurrency,
      headlineFlag: countryCodeToFlag(foreignCurrencyCode),
      sendAmount: utils.currencyFormattedValue(usdAmount, "USD"),
      sendFlag: countryCodeToFlag("US"),
      sendLabel: isUsdSend
        ? l10n.getString("label-currency-amount-send-exactly")
        : l10n.getString("label-currency-amount-send-estimate"),
      exchangeRate: (() => {
        const parsedRate = parseFloat(fxQuote.rate || "NaN");
        if (Number.isNaN(parsedRate)) return null;
        // Mimic useEffectiveConversionRate: invert when
        // converted_currency !== settlement_currency (USD)
        const effectiveRate =
          fxQuote.convertedCurrency !== fxQuote.settlementCurrency
            ? 1 / parsedRate
            : parsedRate;
        return `1 USD = ${effectiveRate.toFixed(4)} ${foreignCurrency}`;
      })(),
    };
  };

  const fxDisplay = hasFxQuote ? getFxDisplayValues() : null;

  return (
    <div className="margin--top--l">
      <ContentCard kind="bordered">
        <h5 className="fontSize--m fontWeight--normal margin--bottom--xs">
          {dualApproval.achReversalId
            ? l10n.getString("approval-drawer-reversal-title", {
                transactionType:
                  originalPayment?.transaction_type ||
                  dualApproval.transactionType ||
                  "credit",
                recipientName: dualApproval.recipientName,
              })
            : l10n.getString("approval-drawer-type-to-recipient", {
                paymentType: typeCopy,
                recipientName: dualApproval.recipientName,
              })}
        </h5>
        {hasFxQuote && fxDisplay ? (
          <h1 className="fontColor--primary">
            {fxDisplay.headlinePrefix}
            {fxDisplay.headlineAmount}{" "}
            <span className="fontSize--m">
              {fxDisplay.headlineFlag} {fxDisplay.headlineCurrency}
            </span>
          </h1>
        ) : (
          <h1 className="fontColor--primary">
            {Filters.currency(dualApproval.dollarAmount, {
              hasDecimal: true,
            })}
          </h1>
        )}
        {hasFxQuote && fxDisplay && (
          <>
            <hr className="margin--bottom--l margin--top--l" />
            <Row>
              <Row.Item>
                <h5 className={styles.drawerLabelHeading}>
                  {fxDisplay.sendLabel}
                </h5>
              </Row.Item>
              <Row.Item shrink>
                {fxDisplay.sendAmount} {fxDisplay.sendFlag} USD
              </Row.Item>
            </Row>
            {fxDisplay.exchangeRate && (
              <Row className="margin--top--s">
                <Row.Item>
                  <h5 className={styles.drawerLabelHeading}>
                    {l10n.getString(
                      "international-wire-form-currency-estimated-exchange-rate",
                    )}
                  </h5>
                </Row.Item>
                <Row.Item shrink>{fxDisplay.exchangeRate}</Row.Item>
              </Row>
            )}
          </>
        )}
        <hr className="margin--bottom--l margin--top--l" />
        {dualApproval.achReversalId ? (
          <ReversalDetails
            dualApproval={dualApproval}
            handleClickOriginalPayment={handleClickOriginalPayment}
          />
        ) : (
          <PaymentScheduleDetails
            dualApproval={dualApproval}
            isPendingDrawer={isPendingDrawer}
          />
        )}
        {dualApproval.internationalWireId && parsedTemplateName ? (
          <>
            <h5 className={cc("margin--top--m", styles.drawerLabelHeading)}>
              {l10n.getString("label-wire-template--template-used")}
            </h5>
            <div className={styles.templateNameRow}>
              <span className={styles.templateNameText}>
                {parsedTemplateName.displayName}
              </span>
              {parsedTemplateName.isDeleted && (
                <Chip
                  kind="warn"
                  label={l10n.getString("label-wire-template--deleted")}
                />
              )}
            </div>
          </>
        ) : null}
      </ContentCard>
    </div>
  );
};

export default TopCard;
